import "twin.macro";

import { useSelector } from "react-redux";

import _ from "lodash";

import { FixedHeightScrollLastChild } from "@components/StyledComponents";
import { Filters, formatDateRange, useFilterParams } from "@features/filters";
import { OrderUploadTaxAndFreight } from "@features/orders";
import {
  PurchaseOrderHistoryTable,
  usePaginatePurchaseOrdersQuery,
} from "@features/purchaseOrders";
import { usePaginatePurchaseOrderVariantsQuery } from "@features/purchaseOrders/data";
import PurchaseOrderHistoryVariantTable from "@features/purchaseOrders/purchaseOrderHistory/PurchaseOrderHistoryVariantTable";
import { RequestReportButton, useReport } from "@features/reports";
import NavTabs from "@features/ui/NavTabs";
import { PurchaseOrderVariantItem } from "@models/PurchaseOrderVariant";
import DocTitle from "@utility/DocTitle";
import permissions from "@utils/permissions";
import useRoleIs from "@utils/useRoleIs";

const poParams = (filters: Record<string, any>) => ({
  filter: {
    searchTerm: filters.q,
    type: filters.orderType,
    groupIds: filters.groups && Object.values(filters.groups).flat(),
    programIds: filters.programs,
    supplierIds: filters.suppliers,
    ...(filters.purchaseOrderStatus && {
      status: filters.purchaseOrderStatus,
    }),
    purchaserIds: filters.purchasers,
    insertedAtRange: formatDateRange(filters),
    hasBeacons: filters.hasBeacons,
    channelId: filters.channel,
    brandIds: filters.brands,
  },
  sort: filters.sort,
});

const poParamsToPovParams = (params: Record<string, any>) => {
  const { filter, sort, ...rest } = params;

  const sortPrefix = sort?.startsWith("-") ? "-" : "";
  const sortKey = sort?.replace(/^-/, "");
  const mappedSortKey =
    {
      "submitted-at": "po-submitted-at",
      "in-market-date": "po-in-market-date",
      type: "po-type",
      id: "po-id",
      status: "po-status",
    }[sortKey] ?? sortKey;

  const mappedFilter = _.mapKeys(filter, (_val, key) => {
    return { type: "poType" }[key] ?? key;
  });

  return {
    sort: sortPrefix + mappedSortKey,
    filter: {
      ...mappedFilter,
      isSetupFee: false,
    },
    ...rest,
  };
};

const POByVariantView = () => {
  const [filters] = useFilterParams();
  const params = poParamsToPovParams(poParams(filters));
  const { data, ...tableProps } = usePaginatePurchaseOrderVariantsQuery(params);
  useReport("purchase-order-history-by-item", [
    "purchase-order-variants",
    poParamsToPovParams(poParams(filters)),
  ]);

  return (
    <PurchaseOrderHistoryVariantTable
      rows={(data ?? []) as PurchaseOrderVariantItem[]}
      {...tableProps}
    />
  );
};

const POByPurchaseOrderView = () => {
  const [filters] = useFilterParams();
  const params = poParams(filters);
  const { data, ...tableProps } = usePaginatePurchaseOrdersQuery(params);
  useReport("purchase-order-history", ["purchase-orders", poParams(filters)]);

  return <PurchaseOrderHistoryTable rows={data ?? []} {...tableProps} />;
};

const PurchaseOrderHistory = ({ view }: { view: "" | "po" }) => {
  const roleIs = useRoleIs();
  const { allowShippingCostsAtOrderLevel, hasBeacons, usesChannels } =
    useSelector((state: any) => state.currentUser.organization);

  return (
    <>
      <DocTitle title={"Purchase Order History"} />
      <FixedHeightScrollLastChild>
        <div tw="flex flex-wrap justify-between items-center">
          <div tw="flex items-center gap-4">
            <h2 tw="text-2xl text-neutral-600 font-bold mb-2">
              Purchase Order History
            </h2>
            <NavTabs
              value={view ?? ""}
              options={[
                { label: "Purchase Order Items", value: "" },
                { label: "Purchase Orders", value: "po" },
              ]}
              nextLocation={(val) => ({
                pathname: `/purchasing/purchase-order-history/${val}`,
              })}
            />
          </div>

          <div tw="flex items-center space-x-2">
            <RequestReportButton
              reportName={
                view === "po"
                  ? `purchase-order-history`
                  : `purchase-order-history-by-item`
              }
            />
            {roleIs("admin", "super", "purchasing-agent") &&
              allowShippingCostsAtOrderLevel && <OrderUploadTaxAndFreight />}
          </div>
        </div>
        <Filters
          searchTitle="Search..."
          slots={[
            "orderType",
            "brands",
            "groups",
            "programs",
            "suppliers",
            "purchaseOrderStatus",
            "purchasers",
            "dateRange",
            hasBeacons && "hasBeacons",
            usesChannels && !roleIs(permissions.supplier) && "channel",
          ]}
          defaultValues={{
            sort: "-id",
            ...(roleIs("purchasing-agent") && {
              purchaseOrderStatus: ["ready-to-purchase"],
            }),
          }}
          alwaysShow={_.compact([
            "purchaseOrderStatus",
            !roleIs(permissions.supplier) && "suppliers",
          ])}
          slotProps={{
            dateRange: {
              chipTextPrefix: "Created ",
              title: "Created Date Range",
            },
          }}
        />

        {view === "" ? <POByVariantView /> : <POByPurchaseOrderView />}
      </FixedHeightScrollLastChild>
    </>
  );
};

export default PurchaseOrderHistory;
